import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';

export interface EndgoodProcessWeightDto {
  id: number;
  endgoodId: number;
  endgoodName?: string;
  processStepId: number;
  processStepName?: string;
  outputWeight: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number;
  updatedBy?: number;
}

export interface EndgoodProcessWeightCreateRequest {
  endgoodId: number;
  processStepId: number;
  outputWeight: number;
  createdBy?: number;
  updatedBy?: number;
}

export interface EndgoodProcessWeightUpdateRequest extends EndgoodProcessWeightCreateRequest {
  id: number;
}

/** Endgood + process step with production but missing process weight */
export interface MissingEndgoodProcessWeightProductionDto {
  endgoodId?: number;
  endgoodName?: string;
  endgoodSku?: string;
  endgoodPartNumber?: string;
  processStepId?: number;
  processName?: string;
  sequenceOrder?: number;
  processFlowId?: number;
  processFlowName?: string;
  branchId?: number;
  branchName?: string;
  workOrderCount?: number;
  operationCount?: number;
  totalQuantityCompleted?: number;
  firstProductionDate?: string;
  lastProductionDate?: string;
}

class EndgoodProcessWeightApiService extends BaseApiService {
  async getByEndgoodId(endgoodId: string, processFlowId?: string | number): Promise<EndgoodProcessWeightDto[]> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodProcessWeightDto[]>>(
        API_ENDPOINTS.ENDGOOD_PROCESS_WEIGHT.BY_ENDGOOD(endgoodId, processFlowId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data) return response.data;
      return [];
    } catch (error) {
      toast.error('Failed to fetch process weights');
      throw error;
    }
  }

  async getMissingWithProduction(
    startDate: string,
    endDate: string,
    branchId?: number
  ): Promise<MissingEndgoodProcessWeightProductionDto[]> {
    try {
      const response = await this.makeRequest<ApiResponse<MissingEndgoodProcessWeightProductionDto[]>>(
        API_ENDPOINTS.ENDGOOD_PROCESS_WEIGHT.MISSING_WITH_PRODUCTION(startDate, endDate, branchId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success && Array.isArray(response.data)) return response.data;
      return [];
    } catch (error) {
      toast.error('Failed to fetch endgoods missing process weight');
      throw error;
    }
  }

  async create(data: EndgoodProcessWeightCreateRequest): Promise<EndgoodProcessWeightDto> {
    const response = await this.makeRequest<ApiResponse<EndgoodProcessWeightDto>>(
      API_ENDPOINTS.ENDGOOD_PROCESS_WEIGHT.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(data) }
    );
    if (!response.success || !response.data) throw new Error(response.message || 'Failed to create process weight');
    toast.success('Process weight added');
    return response.data;
  }

  async update(id: string, data: EndgoodProcessWeightUpdateRequest): Promise<EndgoodProcessWeightDto> {
    const response = await this.makeRequest<ApiResponse<EndgoodProcessWeightDto>>(
      API_ENDPOINTS.ENDGOOD_PROCESS_WEIGHT.UPDATE(id),
      { method: HTTP_METHODS.PUT, body: JSON.stringify(data) }
    );
    if (!response.success || !response.data) throw new Error(response.message || 'Failed to update process weight');
    toast.success('Process weight updated');
    return response.data;
  }

  async delete(id: string): Promise<void> {
    const response = await this.makeRequest<ApiResponse<unknown>>(
      API_ENDPOINTS.ENDGOOD_PROCESS_WEIGHT.DELETE(id),
      { method: HTTP_METHODS.DELETE }
    );
    if (!response.success) throw new Error(response.message || 'Failed to delete process weight');
    toast.success('Process weight removed');
  }
}

export const endgoodProcessWeightApiService = new EndgoodProcessWeightApiService();
